import { useEffect, useRef, useState } from "react";
import Header from "@/components/Header";
import { api, getAxiosErrorMessage, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { isCommercial, ROLE_LABELS } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import UserAdminDialog from "@/components/UserAdminDialog";
import {
  Loader2, Mail, Users as UsersIcon, Plus, Trash2, Shield, X, Image as ImageIcon, Palette,
  ShieldCheck, AlertTriangle, CheckCircle2, Info,
} from "lucide-react";

const EVENT_LABELS = {
  complaint_created: "Création d'un litige",
  complaint_updated: "Modification d'un litige",
  complaint_commented: "Nouveau commentaire",
  complaint_file_added: "Pièce jointe ajoutée",
  complaint_status_changed: "Changement de statut",
  complaint_assigned: "Affectation à un utilisateur",
  complaint_closed: "Clôture de litige",
  corrective_action_created: "Création action corrective",
  corrective_action_updated: "Mise à jour action corrective",
  critical_alert: "Alerte critique (litige critique)",
  verification_due: "Vérification d'efficacité — à échéance",
  verification_reminder: "Vérification d'efficacité — relance automatique",
};

export default function Settings() {
  const { user } = useAuth();
  const branding = useBranding();
  const commercial = isCommercial(user);

  const [cfg, setCfg] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newRecipient, setNewRecipient] = useState("");
  const [userOpen, setUserOpen] = useState(false);
  // Compte dont on administre droits et préférences.
  const [compteAdministre, setCompteAdministre] = useState(null);

  const [brandingForm, setBrandingForm] = useState({ company_name: "" });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  const isAdmin = user?.role === "admin";
  const [routingEmail, setRoutingEmail] = useState("");
  const [diagnostic, setDiagnostic] = useState(null);
  const [routingSaving, setRoutingSaving] = useState(false);
  const canEditBranding = isAdmin || user?.role === "direction";
  const canEmailConfig = !commercial && (user?.role === "admin" || user?.role === "direction" || user?.role === "responsable_qualite");

  const fetchAll = async () => {
    try {
      const reqs = [api.get("/settings/branding/public")];
      if (!commercial) reqs.push(api.get("/settings/email"));
      // L'annuaire complet et le routage des litiges clients sont réservés
      // aux admins : ne pas les demander sinon, le 403 ferait échouer tout le
      // Promise.all et casserait la page.
      if (isAdmin) reqs.push(api.get("/users"));
      if (isAdmin) reqs.push(api.get("/settings/client-complaint-routing"));
      // Diagnostic d'envoi : réservé à l'admin, comme le routage.
      if (isAdmin) reqs.push(api.get("/settings/email/diagnostic"));
      const results = await Promise.all(reqs);
      setBrandingForm({ company_name: results[0].data.company_name || "Prest'Hyg" });
      if (!commercial) setCfg(results[1].data);
      if (isAdmin) {
        setUsers(results[2].data);
        setRoutingEmail(results[3].data.notification_email || "");
        setDiagnostic(results[4].data);
      }
    } finally {
      setLoading(false);
    }
  };

  const saveRouting = async (e) => {
    e.preventDefault();
    setRoutingSaving(true);
    try {
      const { data } = await api.put("/settings/client-complaint-routing", {
        notification_email: routingEmail.trim() || null,
      });
      setRoutingEmail(data.notification_email || "");
      toast.success(
        data.notification_email
          ? "Destinataire enregistré"
          : "Destinataire retiré — les notifications sont désactivées"
      );
    } catch (err) {
      toast.error(getAxiosErrorMessage(err));
    } finally {
      setRoutingSaving(false);
    }
  };

  // Chargement initial uniquement : fetchAll est recréé à chaque rendu, l'ajouter
  // aux dépendances déclencherait une boucle de requêtes.
  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/email", { ...cfg, ...patch });
      setCfg(data);
      toast.success("Paramètres enregistrés");
    } catch (e) { toast.error(getAxiosErrorMessage(e)); }
    finally { setSaving(false); }
  };

  const addRecipient = async () => {
    if (!newRecipient.trim()) return;
    const list = [...(cfg.recipients || []), newRecipient.trim()];
    setNewRecipient("");
    await save({ recipients: list });
  };
  const removeRecipient = async (email) => {
    const list = (cfg.recipients || []).filter((e) => e !== email);
    await save({ recipients: list });
  };
  const toggleEvent = async (key, value) => {
    const events = { ...(cfg.events || {}), [key]: value };
    await save({ events });
  };
  const deleteUser = async (id) => {
    if (!window.confirm("Supprimer cet utilisateur ?")) return;
    try {
      await api.delete(`/users/${id}`);
      fetchAll();
      toast.success("Utilisateur supprimé");
    } catch (e) { toast.error(getAxiosErrorMessage(e)); }
  };

  const uploadCompanyLogo = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Format image requis (PNG/JPG/WebP/SVG)");
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/settings/branding/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await branding.refresh();
      toast.success("Logo entreprise mis à jour");
    } catch (e) { toast.error(getAxiosErrorMessage(e)); }
    finally { setUploadingLogo(false); }
  };

  const removeCompanyLogo = async () => {
    try {
      await api.delete("/settings/branding/logo");
      await branding.refresh();
      toast.success("Logo retiré");
    } catch (e) { toast.error(getAxiosErrorMessage(e)); }
  };

  const saveBrandingName = async () => {
    try {
      await api.put("/settings/branding", { company_name: brandingForm.company_name });
      await branding.refresh();
      toast.success("Nom enregistré");
    } catch (e) { toast.error(getAxiosErrorMessage(e)); }
  };

  return (
    <>
      <Header title="Paramètres" subtitle={commercial ? "Profil & préférences" : "Configuration notifications email · branding · utilisateurs"} />
      <main className="flex-1 p-4 sm:p-6 lg:p-8 space-y-6 max-w-5xl">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
        ) : (
          <>
            {/* Mon mot de passe — accessible à tous les rôles, y compris commercial */}
            <ChangePasswordSection />

            {/* Branding section */}
            <section className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-6">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 font-heading flex items-center gap-2">
                    <Palette size={18} strokeWidth={1.5} /> Identité de l'application
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Personnalisez le logo et le nom affichés dans la sidebar et sur l'écran de connexion.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-6 items-start">
                <div className="flex flex-col items-center gap-3">
                  <div className="h-24 w-24 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center overflow-hidden">
                    <img
                      src={branding.logoUrl}
                      alt="Logo entreprise"
                      className="h-20 w-20 object-contain"
                      data-testid="branding-logo-preview"
                    />
                  </div>
                  {canEditBranding ? (
                    <div className="flex gap-2">
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => uploadCompanyLogo(e.target.files?.[0])}
                        data-testid="branding-logo-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                        className="rounded-xl h-9"
                        data-testid="branding-logo-upload-btn"
                      >
                        {uploadingLogo ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <ImageIcon size={14} className="mr-1.5" />}
                        Remplacer
                      </Button>
                      {branding.logo_file_id ? (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={removeCompanyLogo}
                          className="rounded-xl h-9 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                          data-testid="branding-logo-remove-btn"
                        >
                          <X size={14} />
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                <div className="flex-1 min-w-0 w-full">
                  <Label>Nom affiché</Label>
                  <div className="mt-1.5 flex gap-2">
                    <Input
                      value={brandingForm.company_name}
                      onChange={(e) => setBrandingForm({ company_name: e.target.value })}
                      disabled={!canEditBranding}
                      className="rounded-xl"
                      data-testid="branding-name-input"
                    />
                    {canEditBranding ? (
                      <Button
                        onClick={saveBrandingName}
                        className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                        data-testid="branding-name-save-btn"
                      >
                        Enregistrer
                      </Button>
                    ) : null}
                  </div>
                  {!canEditBranding ? (
                    <p className="text-xs text-slate-400 mt-2">Réservé aux rôles admin / direction.</p>
                  ) : null}
                </div>
              </div>
            </section>

            {isAdmin ? (
              <section
                className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-6"
                data-testid="client-routing-section"
              >
                <h3 className="text-lg font-semibold text-slate-900 font-heading flex items-center gap-2">
                  <Mail size={18} strokeWidth={1.5} /> Destinataire des nouveaux litiges clients
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  Adresse prévenue dès qu'un litige client est enregistré,
                  afin qu'il soit analysé et, le cas échéant, transformé en litige
                  fournisseur ou transporteur.
                </p>

                <div className="mt-4 flex items-center gap-2 text-sm">
                  <span className="text-slate-500">Actuellement configuré :</span>
                  {routingEmail ? (
                    <span
                      className="font-semibold text-slate-900"
                      data-testid="client-routing-current"
                    >
                      {routingEmail}
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-800"
                      data-testid="client-routing-current"
                    >
                      Aucun — notifications désactivées
                    </span>
                  )}
                </div>

                <form onSubmit={saveRouting} className="mt-4 flex flex-wrap items-end gap-3">
                  <div className="flex-1 min-w-[240px]">
                    <Label htmlFor="client-routing-email">Adresse email</Label>
                    <Input
                      id="client-routing-email"
                      type="email"
                      value={routingEmail}
                      onChange={(e) => setRoutingEmail(e.target.value)}
                      placeholder="qualite@exemple.fr"
                      className="rounded-xl mt-1.5"
                      data-testid="client-routing-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={routingSaving}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                    data-testid="client-routing-save-btn"
                  >
                    {routingSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Enregistrer
                  </Button>
                  {routingEmail ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-xl"
                      onClick={() => { setRoutingEmail(""); }}
                      data-testid="client-routing-clear-btn"
                    >
                      Vider
                    </Button>
                  ) : null}
                </form>
                <p className="text-xs text-slate-400 mt-3">
                  Réservé aux administrateurs. Laisser vide puis enregistrer désactive
                  l'envoi ; la création des litiges reste possible dans tous les cas.
                </p>
              </section>
            ) : null}

            {!commercial && cfg ? (
              <>
                {/* Email section */}
                <section className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-6">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 font-heading flex items-center gap-2">
                        <Mail size={18} strokeWidth={1.5} /> Notifications email
                      </h3>
                      <p className="text-sm text-slate-500 mt-1">Configurez qui reçoit les alertes et quand.</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Label htmlFor="email-enabled" className="text-sm">Actif</Label>
                      <Switch
                        id="email-enabled"
                        checked={cfg.enabled}
                        onCheckedChange={(v) => save({ enabled: v })}
                        disabled={!canEmailConfig}
                        data-testid="email-enabled-switch"
                      />
                    </div>
                  </div>

                  {isAdmin && diagnostic ? <DiagnosticEnvoi etat={diagnostic} /> : null}

                  <div className="mb-5">
                    <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Destinataires</Label>
                    <div className="mt-2 flex gap-2">
                      <Input
                        type="email"
                        value={newRecipient}
                        onChange={(e) => setNewRecipient(e.target.value)}
                        placeholder="nouveau@entreprise.fr"
                        className="rounded-xl"
                        disabled={!canEmailConfig}
                        data-testid="new-recipient-input"
                      />
                      <Button
                        onClick={addRecipient}
                        disabled={saving || !canEmailConfig}
                        className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                        data-testid="add-recipient-btn"
                      >
                        <Plus size={16} className="mr-1" /> Ajouter
                      </Button>
                    </div>
                    {(cfg.recipients || []).length > 0 ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {cfg.recipients.map((r) => (
                          <span key={r} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-800 text-sm font-medium">
                            {r}
                            {canEmailConfig ? (
                              <button onClick={() => removeRecipient(r)} className="hover:text-rose-600" data-testid={`remove-recipient-${r}`}>
                                <X size={12} />
                              </button>
                            ) : null}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-3 text-xs text-slate-400">Aucun destinataire configuré.</p>
                    )}
                  </div>

                  <div className="mb-5">
                    <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Événements déclencheurs</Label>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                      {Object.entries(EVENT_LABELS).map(([k, v]) => (
                        <label key={k} className="flex items-center justify-between p-3 rounded-xl border border-slate-100 hover:border-blue-200 transition-colors">
                          <span className="text-sm text-slate-700">{v}</span>
                          <Switch
                            checked={!!cfg.events?.[k]}
                            onCheckedChange={(val) => toggleEvent(k, val)}
                            disabled={!canEmailConfig}
                            data-testid={`event-switch-${k}`}
                          />
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <label className="flex items-center justify-between p-3 rounded-xl border border-slate-100">
                      <span className="text-sm text-slate-700">Alertes critiques instantanées</span>
                      <Switch checked={cfg.critical_alert_enabled} onCheckedChange={(v) => save({ critical_alert_enabled: v })} disabled={!canEmailConfig} data-testid="critical-alert-switch" />
                    </label>
                    <label className="flex items-center justify-between p-3 rounded-xl border border-slate-100">
                      <span className="text-sm text-slate-700">Rappels automatiques</span>
                      <Switch checked={cfg.reminder_enabled} onCheckedChange={(v) => save({ reminder_enabled: v })} disabled={!canEmailConfig} data-testid="reminder-switch" />
                    </label>
                    <label className="flex items-center justify-between p-3 rounded-xl border border-slate-100">
                      <span className="text-sm text-slate-700">Synthèse hebdomadaire</span>
                      <Switch checked={cfg.weekly_summary_enabled} onCheckedChange={(v) => save({ weekly_summary_enabled: v })} disabled={!canEmailConfig} data-testid="weekly-switch" />
                    </label>
                  </div>
                </section>

                {/* Verification of efficacy (ISO 9001) */}
                <section className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-6">
                  <h3 className="text-lg font-semibold text-slate-900 font-heading flex items-center gap-2">
                    <Mail size={18} strokeWidth={1.5} /> Vérification d'efficacité (ISO 9001)
                  </h3>
                  <p className="text-sm text-slate-500 mt-1 mb-5">
                    Notifications automatiques pour les actions correctives permanentes à la date de vérification, avec relance tant que le contrôle n'est pas réalisé.
                  </p>

                  <div className="mb-5">
                    <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">
                      Destinataires supplémentaires (ex. direction)
                    </Label>
                    <VerificationRecipients
                      recipients={cfg.verification_recipients || []}
                      onChange={(list) => save({ verification_recipients: list })}
                      disabled={!canEmailConfig}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <label className="flex items-center justify-between p-3 rounded-xl border border-slate-100">
                      <span className="text-sm text-slate-700">Notifier le responsable</span>
                      <Switch
                        checked={cfg.verification_notify_responsible !== false}
                        onCheckedChange={(v) => save({ verification_notify_responsible: v })}
                        disabled={!canEmailConfig}
                        data-testid="verif-notify-responsible-switch"
                      />
                    </label>
                    <div className="p-3 rounded-xl border border-slate-100">
                      <div className="text-xs text-slate-400 uppercase tracking-wider font-bold mb-1.5">Intervalle relances (jours)</div>
                      <Input
                        type="number"
                        min={1}
                        max={60}
                        value={cfg.verification_reminder_interval_days ?? 7}
                        onChange={(e) => save({ verification_reminder_interval_days: Math.max(1, parseInt(e.target.value) || 7) })}
                        disabled={!canEmailConfig}
                        className="rounded-lg h-9"
                        data-testid="verif-interval-input"
                      />
                    </div>
                    <div className="p-3 rounded-xl border border-slate-100">
                      <div className="text-xs text-slate-400 uppercase tracking-wider font-bold mb-1.5">Nombre max de relances</div>
                      <Input
                        type="number"
                        min={0}
                        max={10}
                        value={cfg.verification_max_reminders ?? 3}
                        onChange={(e) => save({ verification_max_reminders: Math.max(0, parseInt(e.target.value) || 3) })}
                        disabled={!canEmailConfig}
                        className="rounded-lg h-9"
                        data-testid="verif-max-input"
                      />
                    </div>
                  </div>
                </section>

                {/* Users section — admin only (l'annuaire n'est pas exposé aux autres rôles) */}
                {isAdmin ? (
                <section className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-6">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 font-heading flex items-center gap-2">
                        <UsersIcon size={18} strokeWidth={1.5} /> Utilisateurs & rôles
                      </h3>
                      <p className="text-sm text-slate-500 mt-1">
                        Comptes administratifs (complet) vs commerciaux (accès restreint aux litiges clients uniquement).
                      </p>
                    </div>
                    {user?.role === "admin" ? (
                      <Dialog open={userOpen} onOpenChange={setUserOpen}>
                        <DialogTrigger asChild>
                          <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl" data-testid="add-user-btn">
                            <Plus size={16} className="mr-1.5" /> Nouvel utilisateur
                          </Button>
                        </DialogTrigger>
                        <UserDialog
                          onClose={() => setUserOpen(false)}
                          onSaved={() => { setUserOpen(false); fetchAll(); }}
                        />
                      </Dialog>
                    ) : null}
                  </div>
                  <div className="space-y-2">
                    {users.map((u) => (
                      <div key={u.id} className="flex items-center gap-4 p-3 rounded-xl border border-slate-100" data-testid={`user-row-${u.id}`}>
                        <div className="h-9 w-9 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center text-sm font-semibold">
                          {u.name?.[0]?.toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">{u.name}</div>
                          <div className="text-xs text-slate-500 truncate">{u.email}</div>
                        </div>
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${u.role === "commercial" ? "bg-amber-50 text-amber-800" : "bg-slate-100 text-slate-700"}`}>
                          <Shield size={11} /> {ROLE_LABELS[u.role] || u.role}
                        </span>
                        {user?.role === "admin" ? (
                          <button
                            type="button"
                            onClick={() => setCompteAdministre(u)}
                            className="rounded-lg px-2 py-1 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-50"
                            data-testid={`admin-user-${u.id}`}
                            title="Droits et notifications de ce compte"
                          >
                            Droits &amp; notifications
                          </button>
                        ) : null}
                        {user?.role === "admin" && u.id !== user.id ? (
                          <button
                            onClick={() => deleteUser(u.id)}
                            className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-colors"
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </section>
                ) : null}

                <Dialog
                  open={!!compteAdministre}
                  onOpenChange={(v) => !v && setCompteAdministre(null)}
                >
                  {compteAdministre ? (
                    <UserAdminDialog
                      utilisateur={compteAdministre}
                      onClose={() => setCompteAdministre(null)}
                    />
                  ) : null}
                </Dialog>
              </>
            ) : null}
          </>
        )}
      </main>
    </>
  );
}

const REGLE_MOT_DE_PASSE =
  "12 caractères minimum, combinant au moins 3 types parmi minuscules, majuscules, " +
  "chiffres et caractères spéciaux. Sont refusés les termes évidents, les suites de " +
  "clavier, les répétitions, et tout mot de passe reprenant votre nom ou votre email.";

function ChangePasswordSection() {
  const vide = { current_password: "", new_password: "", confirm_password: "" };
  const [form, setForm] = useState(vide);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Contrôle de confirmation côté navigateur, à titre de confort : la règle qui
  // fait foi est appliquée par le backend, qui renvoie le détail des refus.
  const confirmationDivergente =
    form.confirm_password.length > 0 && form.new_password !== form.confirm_password;

  const submit = async (e) => {
    e.preventDefault();
    if (confirmationDivergente) return;
    setSaving(true);
    try {
      await api.post("/auth/change-password", form);
      setForm(vide);
      toast.success("Mot de passe modifié. Vos autres sessions ont été déconnectées.");
    } catch (err) {
      toast.error(getAxiosErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-6"
      data-testid="change-password-section"
    >
      <h3 className="text-lg font-semibold text-slate-900 font-heading flex items-center gap-2">
        <ShieldCheck size={18} strokeWidth={1.5} /> Mon mot de passe
      </h3>
      <p className="text-sm text-slate-500 mt-1 mb-5">
        Vous seul pouvez modifier votre mot de passe depuis cet écran.
      </p>

      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
        <div>
          <Label>Mot de passe actuel</Label>
          <Input
            type="password"
            autoComplete="current-password"
            value={form.current_password}
            onChange={(e) => set("current_password", e.target.value)}
            required
            className="rounded-xl mt-1.5"
            data-testid="current-password-input"
          />
        </div>
        <div>
          <Label>Nouveau mot de passe</Label>
          <Input
            type="password"
            autoComplete="new-password"
            value={form.new_password}
            onChange={(e) => set("new_password", e.target.value)}
            required
            minLength={12}
            className="rounded-xl mt-1.5"
            data-testid="new-password-input"
          />
        </div>
        <div>
          <Label>Confirmer</Label>
          <Input
            type="password"
            autoComplete="new-password"
            value={form.confirm_password}
            onChange={(e) => set("confirm_password", e.target.value)}
            required
            minLength={12}
            className="rounded-xl mt-1.5"
            data-testid="confirm-password-input"
          />
          {confirmationDivergente ? (
            <p className="text-xs text-red-600 mt-1.5" data-testid="confirm-password-error">
              La confirmation ne correspond pas.
            </p>
          ) : null}
        </div>

        <div className="sm:col-span-3 flex flex-wrap items-center gap-4">
          <Button
            type="submit"
            disabled={saving || confirmationDivergente}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
            data-testid="change-password-submit"
          >
            {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Modification…</> : "Modifier le mot de passe"}
          </Button>
          <p className="text-xs text-slate-500 flex-1 min-w-[16rem]" data-testid="password-policy-hint">
            {REGLE_MOT_DE_PASSE}
          </p>
        </div>
      </form>
    </section>
  );
}

function VerificationRecipients({ recipients, onChange, disabled }) {
  const [input, setInput] = useState("");
  const add = async () => {
    if (!input.trim()) return;
    const list = [...recipients, input.trim()];
    setInput("");
    onChange(list);
  };
  const remove = async (email) => {
    onChange(recipients.filter((r) => r !== email));
  };
  return (
    <div className="mt-2">
      <div className="flex gap-2">
        <Input
          type="email"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="direction@entreprise.fr"
          className="rounded-xl"
          disabled={disabled}
          data-testid="verif-recipient-input"
        />
        <Button
          onClick={add}
          disabled={disabled || !input.trim()}
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
          data-testid="verif-add-recipient-btn"
        >
          <Plus size={16} className="mr-1" /> Ajouter
        </Button>
      </div>
      {recipients.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {recipients.map((r) => (
            <span key={r} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-800 text-sm font-medium">
              {r}
              {!disabled ? (
                <button onClick={() => remove(r)} className="hover:text-rose-600" data-testid={`verif-remove-${r}`}>
                  <X size={12} />
                </button>
              ) : null}
            </span>
          ))}
        </div>
      ) : (
        <p className="mt-2 text-xs text-slate-400">
          Par défaut, seul le responsable de l'action est notifié (si activé ci-dessous).
        </p>
      )}
    </div>
  );
}

function UserDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({ email: "", name: "", role: "commercial", password: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("Utilisateur créé");
      onSaved();
    } catch (err) { toast.error(getAxiosErrorMessage(err)); }
    finally { setSaving(false); }
  };
  return (
    <DialogContent className="max-w-md rounded-2xl">
      <DialogHeader><DialogTitle className="font-heading">Nouvel utilisateur</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label>Nom</Label>
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} required className="rounded-xl mt-1.5" data-testid="user-name-input" />
        </div>
        <div>
          <Label>Email</Label>
          <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className="rounded-xl mt-1.5" data-testid="user-email-input" />
        </div>
        <div>
          <Label>Mot de passe</Label>
          <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={12} className="rounded-xl mt-1.5" data-testid="user-password-input" />
          {/* Rappel d'usage : la règle qui fait foi est appliquée par le backend,
              qui renvoie le détail des refus (affiché en toast). */}
          <p className="text-xs text-slate-500 mt-1.5" data-testid="user-password-policy">
            12 caractères minimum, combinant au moins 3 types parmi minuscules,
            majuscules, chiffres et caractères spéciaux. Sont refusés : les termes
            évidents (« admin », nom de l'entreprise…), les suites de clavier, les
            répétitions, et tout mot de passe reprenant le nom ou l'email du compte.
          </p>
        </div>
        <div>
          <Label>Rôle</Label>
          <Select value={form.role} onValueChange={(v) => set("role", v)}>
            <SelectTrigger className="rounded-xl mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(ROLE_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500 mt-1.5">
            <strong>Commercial</strong> = accès restreint (litiges clients uniquement). Les autres rôles sont administratifs.
          </p>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>Annuler</Button>
          <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl" data-testid="user-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Créer
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}


/**
 * État de la chaîne d'envoi des emails.
 *
 * Cet écran existe parce que l'application ne permettait pas de répondre à
 * « pourquoi n'ai-je rien reçu ? ». Il a fallu ouvrir une session sur le
 * serveur et lire les journaux ligne à ligne pour découvrir que la clé d'API
 * était absente — alors que tout le reste fonctionnait.
 *
 * Aucun secret n'est affiché : la présence de la clé, jamais sa valeur ; le
 * domaine de l'expéditeur, jamais son adresse.
 */
function DiagnosticEnvoi({ etat }) {
  const bloquantes = (etat.anomalies || []).filter((a) => a.niveau === "bloquant");
  const avertissements = (etat.anomalies || []).filter((a) => a.niveau === "avertissement");
  const pret = etat.pret_a_envoyer;

  return (
    <div
      className={`mb-5 rounded-xl border p-4 ${
        pret ? "border-emerald-200 bg-emerald-50/50" : "border-rose-200 bg-rose-50/50"
      }`}
      data-testid="email-diagnostic"
    >
      <div className="flex items-start gap-2.5">
        {pret ? (
          <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-emerald-600" />
        ) : (
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-rose-600" />
        )}
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-semibold ${pret ? "text-emerald-900" : "text-rose-900"}`}>
            {pret
              ? "Les emails peuvent partir."
              : "Aucun email ne peut partir en l'état."}
          </p>

          {bloquantes.length > 0 ? (
            <ul className="mt-2 space-y-1">
              {bloquantes.map((a) => (
                <li key={a.message} className="text-sm text-rose-800" data-testid="diagnostic-bloquant">
                  {a.message}
                </li>
              ))}
            </ul>
          ) : null}

          {avertissements.length > 0 ? (
            <ul className="mt-2 space-y-1">
              {avertissements.map((a) => (
                <li
                  key={a.message}
                  className="flex items-start gap-1.5 text-sm text-amber-800"
                  data-testid="diagnostic-avertissement"
                >
                  <Info size={13} className="mt-0.5 shrink-0" />
                  <span>{a.message}</span>
                </li>
              ))}
            </ul>
          ) : null}

          <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-600 sm:grid-cols-4">
            <div>
              <dt className="text-slate-400">Clé d'envoi</dt>
              <dd className="font-semibold text-slate-800" data-testid="diagnostic-cle">
                {etat.cle_api?.presente ? "Présente" : "Absente"}
              </dd>
            </div>
            <div>
              <dt className="text-slate-400">Domaine expéditeur</dt>
              <dd className="font-semibold text-slate-800">
                {etat.expediteur?.domaine || "—"}
              </dd>
            </div>
            <div>
              <dt className="text-slate-400">Événements actifs</dt>
              <dd className="font-semibold text-slate-800">
                {etat.notifications?.evenements_actifs} / {etat.notifications?.evenements_total}
              </dd>
            </div>
            <div>
              <dt className="text-slate-400">Destinataires</dt>
              <dd className="font-semibold text-slate-800">
                {etat.destinataires?.generaux ?? 0}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}
